import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Card,
  Avatar,
  Typography,
  Box,
  InputAdornment,
  Skeleton,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Search,
  Assignment,
  Visibility,
  VideoCall,
  Business,
  CalendarToday,
  CheckCircle,
  Chat as ChatIcon,
  Refresh,
  Person,
  Close,
  Email,
  Phone,
  Subject,
  Description,
  AttachFile,
  GetApp,
  Send
} from '@mui/icons-material';
import axios from 'axios';
import { useSocket } from '../../components/Hooks/SocketProvider';
import { BASE_URL } from '../../services/configUrls';

const MyAssignedTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalMyTickets, setTotalMyTickets] = useState(0);
  
  // View Dialog States
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  
  // Solve Dialog States
  const [openSolveDialog, setOpenSolveDialog] = useState(false);
  const [closingRemark, setClosingRemark] = useState('');
  const [solvingLoading, setSolvingLoading] = useState(false);
  const [solveError, setSolveError] = useState(null);
  const [solveSuccess, setSolveSuccess] = useState(null);

  // Chat Dialog States
  const [openChatDialog, setOpenChatDialog] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [loadingChat, setLoadingChat] = useState(false);
  const [chatError, setChatError] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  const { socket, lastMessage, userId, connectionStatus } = useSocket();

  const chatMessagesRef = useRef(chatMessages);
  const chatModalRequestRef = useRef(null);
  const isChatModalOpenRef = useRef(openChatDialog);
  const chatMessagesEndRef = useRef(null);

  useEffect(() => {
    chatMessagesRef.current = chatMessages;
    scrollToBottom();
  }, [chatMessages]);

  useEffect(() => {
    chatModalRequestRef.current = selectedTicket;
  }, [selectedTicket]);

  useEffect(() => {
    isChatModalOpenRef.current = openChatDialog;
  }, [openChatDialog]);

  const getAccessToken = () => {
    return localStorage.getItem('accessToken') || 'YOUR_TOKEN_HERE';
  };

  const scrollToBottom = () => {
    chatMessagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // WebSocket listener
  useEffect(() => {
    if (!socket) return;

    const handleWebSocketMessage = (event) => {
      try {
        let messageData;
        if (typeof event.data === 'object') {
          messageData = event.data;
        } else if (typeof event.data === 'string') {
          let parsed = event.data;
          let attempts = 0;
          const maxAttempts = 3;
          while (typeof parsed === "string" && attempts < maxAttempts) {
            try {
              const nextParse = JSON.parse(parsed);
              parsed = nextParse;
              attempts++;
            } catch (e) {
              break;
            }
          }
          messageData = parsed;
        } else {
          return;
        }

        const isChatOpen = isChatModalOpenRef.current;
        const currentTicket = chatModalRequestRef.current;
        const currentTicketId = currentTicket?.meeting_id;

        if (messageData.chat_response) {
          const chatResponse = messageData.chat_response;
          const messageTicketId = chatResponse.staff_meting_id || chatResponse.staff_meeting_id;

          if (isChatOpen && currentTicketId && String(messageTicketId) === String(currentTicketId)) {
            setChatMessages((prevMessages) => {
              const isOwnMessage = String(chatResponse.made_by_user_id) === String(userId);
              let filteredMessages = prevMessages;
              
              if (isOwnMessage) {
                filteredMessages = prevMessages.filter((msg) => !msg.sending);
              }

              const messageExists = filteredMessages.some(
                (msg) =>
                  msg.id === chatResponse.id ||
                  (msg.message === chatResponse.message &&
                    Math.abs(new Date(msg.created_at) - new Date(chatResponse.created_at)) < 1000)
              );

              if (messageExists) {
                return filteredMessages;
              }

              return [...filteredMessages, chatResponse];
            });
          }
        }
      } catch (error) {
        console.error("Error in WebSocket handler:", error);
      }
    };

    socket.addEventListener("message", handleWebSocketMessage);

    return () => {
      socket.removeEventListener("message", handleWebSocketMessage);
    };
  }, [socket, userId]);

  const fetchChatMessages = async (meetingId) => {
    setLoadingChat(true);
    setChatError(null);
    
    try {
      const config = {
        method: 'get',
        maxBodyLength: Infinity,
        url: `${BASE_URL}/staffmetting/staffMeetingChat/${meetingId}`,
        headers: { 
          'Authorization': `Bearer ${getAccessToken()}`
        }
      };

      const response = await axios.request(config);
      
      if (response.data && response.data.messages) {
        setChatMessages(response.data.messages);
      } else {
        setChatMessages([]);
      }
    } catch (error) {
      console.error('Error fetching chat messages:', error);
      if (error.response) {
        setChatError(`Error ${error.response.status}: ${error.response.data?.message || 'Unable to load chat messages'}`);
      } else if (error.request) {
        setChatError('Network error. Please check your connection.');
      } else {
        setChatError('Unable to load chat messages. Please try again.');
      }
    } finally {
      setLoadingChat(false);
    }
  };

  const sendChatMessage = async () => {
    if (!newMessage.trim() || !selectedTicket) return;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      setChatError('Chat connection not available. Please refresh the page.');
      return;
    }

    setSendingMessage(true);
    
    try {
      const messageData = {
        staff_meting_id: selectedTicket.meeting_id,
        message: newMessage.trim(),
        made_by_user_id: userId,
        role: "staff",
        is_spoc: "0"
      };

      const tempMessage = {
        id: `temp-${Date.now()}`,
        message: newMessage.trim(),
        role: "staff",
        is_spoc: "0",
        created_at: new Date().toISOString(),
        made_by_user_id: userId,
        staff_meting_id: selectedTicket.meeting_id,
        sending: true
      };
      
      setChatMessages(prev => [...prev, tempMessage]);
      socket.send(JSON.stringify(messageData));
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      setChatError('Failed to send message. Please try again.');
      setChatMessages(prev => prev.filter(msg => !msg.sending));
    } finally {
      setSendingMessage(false);
    }
  };

  const fetchMyAssignedTickets = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const config = {
        method: 'get',
        maxBodyLength: Infinity,
        url: `${BASE_URL}/staffmetting/myAssignedTickets`,
        headers: { 
          'Authorization': `Bearer ${getAccessToken()}`
        }
      };

      const response = await axios.request(config);
      console.log('My Assigned Tickets Response:', response.data);
      
      if (response.data && response.data.data) {
        setTickets(response.data.data);
        setTotalMyTickets(response.data.total_my_tickets || 0);
      } else {
        setTickets([]);
        setTotalMyTickets(0);
      }
    } catch (error) {
      console.error('Error fetching my tickets:', error);
      if (error.response) {
        const status = error.response.status;
        if (status === 403) {
          setError('You are not authorized as a support user. Please contact administrator.');
        } else if (status === 404) {
          setError('User not found. Please login again.');
        } else {
          setError(`Error ${status}: ${error.response.data?.detail || 'Unable to load tickets'}`);
        }
      } else if (error.request) {
        setError('Network error. Please check your connection.');
      } else {
        setError('Unable to load your assigned tickets.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyAssignedTickets();
  }, []);

  const filteredTickets = tickets.filter(ticket => 
    ticket.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ticket.topic?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ticket.institute_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ticket.student_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleJoinMeeting = (meetingLink) => {
    if (!meetingLink) {
      alert("Meeting link is not available");
      return;
    }
    
    try {
      let url = meetingLink.trim();
      if (!url.startsWith("http://") && !url.startsWith("https://")) {
        url = `https://${url}`;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error("Error opening meeting link:", error);
      alert("Failed to open meeting link.");
    }
  };

  const handleOpenViewDialog = (ticket) => {
    setSelectedTicket(ticket);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setSelectedTicket(null);
  };

  const handleOpenChatDialog = (ticket) => {
    chatModalRequestRef.current = ticket;
    isChatModalOpenRef.current = true;
    setSelectedTicket(ticket);
    setOpenChatDialog(true);
    setChatMessages([]);
    setNewMessage("");
    setChatError(null);
    fetchChatMessages(ticket.meeting_id);
  };

  const handleCloseChatDialog = () => {
    setOpenChatDialog(false);
    setSelectedTicket(null);
    isChatModalOpenRef.current = false;
    chatModalRequestRef.current = null;
    setChatMessages([]);
    setNewMessage("");
    setChatError(null);
  };

  const handleOpenSolveDialog = (ticket) => {
    setSelectedTicket(ticket);
    setClosingRemark('');
    setSolveError(null);
    setSolveSuccess(null);
    setOpenSolveDialog(true);
  };

  const handleCloseSolveDialog = () => {
    setOpenSolveDialog(false);
    setSelectedTicket(null);
    setClosingRemark('');
    setSolveError(null);
    setSolveSuccess(null);
  };

  const handleSolveMeeting = async () => {
    if (!closingRemark.trim()) {
      setSolveError('Please provide a closing remark explaining how the issue has been resolved.');
      return;
    }

    setSolvingLoading(true);
    setSolveError(null);

    try {
      const data = JSON.stringify({
        staff_meetings_id: selectedTicket.meeting_id,
        status: "solved",
        closing_remark: closingRemark.trim()
      });

      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: `${BASE_URL}/staffmetting/updateSupportQuestion_3`,
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${getAccessToken()}`
        },
        data: data
      };

      const response = await axios.request(config);
      
      if (response.data.message === "successfully updated" || response.status === 200) {
        setSolveSuccess('Ticket has been successfully marked as solved!');
        
        setTickets(prevTickets => 
          prevTickets.filter(ticket => ticket.meeting_id !== selectedTicket.meeting_id)
        );

        setTotalMyTickets(prev => Math.max(0, prev - 1));

        setTimeout(() => {
          handleCloseSolveDialog();
        }, 2000);
      } else {
        setSolveError('Failed to update ticket status. Please try again.');
      }
    } catch (error) {
      console.error('Error solving ticket:', error);
      if (error.response) {
        const status = error.response.status;
        if (status === 401) {
          setSolveError('Authentication failed. Please login again.');
        } else if (status === 403) {
          setSolveError('You do not have permission to update this ticket.');
        } else {
          setSolveError(`Error ${status}: ${error.response.data?.message || error.response.data?.detail || 'Failed to update ticket status'}`);
        }
      } else if (error.request) {
        setSolveError('Network error. Please check your connection and try again.');
      } else {
        setSolveError('Error occurred while updating ticket status. Please try again.');
      }
    } finally {
      setSolvingLoading(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatChatTimestamp = (dateString) => {
    try {
      return new Date(dateString).toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return dateString;
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(word => word[0]).join('').toUpperCase() || 'NA';
  };

  const getMessageSenderInfo = (message) => {
    const isSpoc = message.is_spoc === "1" || message.is_spoc === 1;
    return {
      isSpoc,
      label: isSpoc ? 'SPOC' : 'Support',
      color: isSpoc ? '#3b82f6' : '#10b981',
      bgColor: isSpoc ? '#dbeafe' : '#d1fae5',
      avatarBg: isSpoc ? 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
    };
  };

  const getFileIcon = (url) => {
    const extension = url?.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf': return '📄';
      case 'doc':
      case 'docx': return '📝';
      case 'xls':
      case 'xlsx': return '📊';
      case 'png':
      case 'jpg':
      case 'jpeg': return '🖼️';
      case 'pages': return '📃';
      default: return '📎';
    }
  };

  const getFileName = (url) => {
    if (!url) return 'Unknown File';
    const fileName = url.split('/').pop();
    const cleanFileName = fileName.replace(/^[a-f0-9\-]{36}/i, '');
    return cleanFileName || fileName;
  };

  const handleDownloadAttachment = (attachment) => {
    if (attachment.attachment_name) {
      window.open(attachment.attachment_name, '_blank');
    }
  };

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: '#f8fafc' }}>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg">
              <Person className="text-white text-lg" />
            </div>
            <div>
              <Typography variant="h5" className="font-bold text-gray-900 tracking-tight">
                My Assigned Tickets
              </Typography>
              <Typography variant="body2" className="text-gray-500 font-medium text-xs">
                Total: {totalMyTickets} tickets assigned to you
              </Typography>
            </div>
          </div>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchMyAssignedTickets}
            size="small"
            style={{
              textTransform: 'none',
              borderRadius: '8px',
              fontSize: '12px'
            }}
          >
            Refresh
          </Button>
        </div>

        {/* WebSocket Connection Status */}
        <Card className="border-0 shadow-lg shadow-black/5 mb-4">
          <Box className="p-3 flex items-center gap-2">
            <div 
              className={`w-2 h-2 rounded-full ${
                connectionStatus === 'connected' ? 'bg-green-500' : 
                connectionStatus === 'connecting' ? 'bg-yellow-500' : 
                'bg-red-500'
              }`}
            />
            <Typography variant="body2" style={{ fontSize: '11px' }}>
              WebSocket: {connectionStatus} {userId && `(User ID: ${userId})`}
            </Typography>
          </Box>
        </Card>

        {/* Search */}
        <Card className="border-0 shadow-lg shadow-black/5 mb-6">
          <Box className="p-4">
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by student name, topic, institute, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search className="text-gray-400" />
                  </InputAdornment>
                ),
                className: "rounded-lg bg-gray-50",
                style: { fontSize: '12px' }
              }}
            />
          </Box>
        </Card>
      </div>

      {/* Error Message */}
      {error && (
        <Card className="border-0 shadow-lg mb-6 bg-red-50 border-red-200">
          <Box className="p-4">
            <Alert severity="error">
              <Typography variant="body2" className="text-red-700">
                {error}
              </Typography>
            </Alert>
            <Button
              size="small"
              onClick={fetchMyAssignedTickets}
              className="mt-2"
              variant="outlined"
              color="error"
            >
              Retry
            </Button>
          </Box>
        </Card>
      )}

      {/* Table */}
      <Card className="border-0 shadow-lg shadow-black/5">
        <TableContainer component={Paper} style={{ borderRadius: '12px', overflow: 'hidden' }}>
          <Table>
            <TableHead>
              <TableRow style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)' }}>
                <TableCell style={{ color: 'white', fontWeight: 'bold', fontSize: '11px' }}>Ticket ID</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold', fontSize: '11px' }}>Student</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold', fontSize: '11px' }}>Topic</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold', fontSize: '11px' }}>Description</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold', fontSize: '11px' }}>Institute</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold', fontSize: '11px' }}>Assigned On</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold', fontSize: '11px', textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from(new Array(5)).map((_, index) => (
                  <TableRow key={index}>
                    {Array.from(new Array(7)).map((_, cellIndex) => (
                      <TableCell key={cellIndex}>
                        <Skeleton animation="wave" height={30} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : filteredTickets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Box className="py-12">
                      <Assignment style={{ fontSize: '64px', color: '#d1d5db' }} />
                      <Typography variant="body1" className="mt-4 text-gray-500" style={{ fontSize: '14px' }}>
                        {searchTerm ? 'No tickets found matching your search.' : 'No tickets assigned to you yet.'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTickets.map((ticket) => (
                  <TableRow key={ticket.meeting_id} hover style={{ cursor: 'pointer' }}>
                    <TableCell style={{ fontSize: '11px', fontWeight: 'bold' }}>
                      <Chip 
                        label={`#${ticket.meeting_id}`} 
                        size="small" 
                        style={{ 
                          backgroundColor: '#dbeafe', 
                          color: '#1e40af',
                          fontSize: '10px',
                          height: '22px',
                          fontWeight: 'bold'
                        }} 
                      />
                    </TableCell>
                    <TableCell style={{ fontSize: '11px' }}>
                      <div className="flex items-center gap-2">
                        <Avatar 
                          className="w-7 h-7"
                          style={{ 
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            fontSize: '10px'
                          }}
                        >
                          {getInitials(ticket.student_name)}
                        </Avatar>
                        <div>
                          <Typography variant="body2" style={{ fontSize: '11px', fontWeight: 'bold' }}>
                            {ticket.student_name}
                          </Typography>
                          <Typography variant="caption" style={{ fontSize: '9px', color: '#6b7280' }}>
                            {ticket.student_email}
                          </Typography>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell style={{ fontSize: '11px' }}>
                      <Typography variant="body2" style={{ fontSize: '11px', fontWeight: '500', maxWidth: '200px' }}>
                        {ticket.topic || 'No topic'}
                      </Typography>
                    </TableCell>
                    <TableCell style={{ fontSize: '11px' }}>
                      <Typography 
                        variant="body2" 
                        style={{ 
                          fontSize: '10px', 
                          color: '#6b7280',
                          maxWidth: '250px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                        title={ticket.description}
                      >
                        {ticket.description || 'No description'}
                      </Typography>
                    </TableCell>
                    <TableCell style={{ fontSize: '11px' }}>
                      <div className="flex items-center gap-1">
                        <Business style={{ fontSize: '14px', color: '#9ca3af' }} />
                        <Typography variant="body2" style={{ fontSize: '11px' }}>
                          {ticket.institute_name || 'N/A'}
                        </Typography>
                      </div>
                    </TableCell>
                    <TableCell style={{ fontSize: '11px' }}>
                      <div className="flex items-center gap-1">
                        <CalendarToday style={{ fontSize: '14px', color: '#9ca3af' }} />
                        <Typography variant="caption" style={{ fontSize: '10px' }}>
                          {formatDate(ticket.assignment_created_at || ticket.updated_at)}
                        </Typography>
                      </div>
                    </TableCell>
                    <TableCell style={{ fontSize: '11px' }}>
                      <div className="flex gap-1 justify-center">
                        {ticket.metting_link && (
                          <Tooltip title="Join Meeting">
                            <IconButton 
                              size="small"
                              onClick={() => handleJoinMeeting(ticket.metting_link)}
                              style={{ 
                                backgroundColor: '#dbeafe', 
                                color: '#1e40af',
                                width: '28px',
                                height: '28px'
                              }}
                            >
                              <VideoCall style={{ fontSize: '14px' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small"
                            onClick={() => handleOpenViewDialog(ticket)}
                            style={{ 
                              backgroundColor: '#f3e8ff', 
                              color: '#7c3aed',
                              width: '28px',
                              height: '28px'
                            }}
                          >
                            <Visibility style={{ fontSize: '14px' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Chat">
                          <IconButton 
                            size="small"
                            onClick={() => handleOpenChatDialog(ticket)}
                            style={{ 
                              backgroundColor: '#d1fae5', 
                              color: '#065f46',
                              width: '28px',
                              height: '28px'
                            }}
                          >
                            <ChatIcon style={{ fontSize: '14px' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Mark as Solved">
                          <IconButton 
                            size="small"
                            onClick={() => handleOpenSolveDialog(ticket)}
                            style={{ 
                              backgroundColor: '#dcfce7', 
                              color: '#16a34a',
                              width: '28px',
                              height: '28px'
                            }}
                          >
                            <CheckCircle style={{ fontSize: '14px' }} />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* View Dialog */}
      <Dialog 
        open={openViewDialog} 
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ style: { borderRadius: 16 } }}
      >
        <DialogTitle 
          style={{ 
            fontSize: '18px', 
            fontWeight: 'bold', 
            color: '#1f2937',
            borderBottom: '2px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div className="flex items-center gap-2">
            <Assignment style={{ fontSize: '22px', color: '#3b82f6' }} />
            <Typography variant="h6" style={{ fontSize: '16px', fontWeight: 'bold' }}>
              Ticket Details #{selectedTicket?.meeting_id}
            </Typography>
          </div>
          <IconButton size="small" onClick={handleCloseViewDialog}>
            <Close style={{ fontSize: '20px' }} />
          </IconButton>
        </DialogTitle>
        <DialogContent className="pt-4">
          {selectedTicket && (
            <Box>
              {/* Student Information */}
              <Card style={{ marginBottom: 16, padding: 16, borderRadius: 12, backgroundColor: '#f9fafb' }}>
                <Typography variant="subtitle2" style={{ fontSize: '13px', fontWeight: 'bold', color: '#374151', marginBottom: 12 }}>
                  Student Information
                </Typography>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <Person style={{ fontSize: '16px', color: '#9ca3af' }} />
                    <div>
                      <Typography variant="caption" style={{ fontSize: '10px', color: '#6b7280' }}>Name</Typography>
                      <Typography variant="body2" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                        {selectedTicket.student_name}
                      </Typography>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Email style={{ fontSize: '16px', color: '#9ca3af' }} />
                    <div>
                      <Typography variant="caption" style={{ fontSize: '10px', color: '#6b7280' }}>Email</Typography>
                      <Typography variant="body2" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                        {selectedTicket.student_email}
                      </Typography>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Business style={{ fontSize: '16px', color: '#9ca3af' }} />
                    <div>
                      <Typography variant="caption" style={{ fontSize: '10px', color: '#6b7280' }}>Institute</Typography>
                      <Typography variant="body2" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                        {selectedTicket.institute_name || 'N/A'}
                      </Typography>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Ticket Details */}
              <Card style={{ marginBottom: 16, padding: 16, borderRadius: 12, backgroundColor: '#f9fafb' }}>
                <Typography variant="subtitle2" style={{ fontSize: '13px', fontWeight: 'bold', color: '#374151', marginBottom: 12 }}>
                  Ticket Details
                </Typography>
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <Subject style={{ fontSize: '16px', color: '#9ca3af', marginTop: 2 }} />
                    <div style={{ flex: 1 }}>
                      <Typography variant="caption" style={{ fontSize: '10px', color: '#6b7280' }}>Topic</Typography>
                      <Typography variant="body2" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                        {selectedTicket.topic || 'No topic'}
                      </Typography>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Description style={{ fontSize: '16px', color: '#9ca3af', marginTop: 2 }} />
                    <div style={{ flex: 1 }}>
                      <Typography variant="caption" style={{ fontSize: '10px', color: '#6b7280' }}>Description</Typography>
                      <Typography variant="body2" style={{ fontSize: '12px' }}>
                        {selectedTicket.description || 'No description'}
                      </Typography>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <CalendarToday style={{ fontSize: '16px', color: '#9ca3af' }} />
                    <div>
                      <Typography variant="caption" style={{ fontSize: '10px', color: '#6b7280' }}>Created At</Typography>
                      <Typography variant="body2" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                        {formatDate(selectedTicket.created_at)}
                      </Typography>
                    </div>
                  </div>
                  {selectedTicket.metting_link && (
                    <div className="flex items-center gap-2">
                      <VideoCall style={{ fontSize: '16px', color: '#9ca3af' }} />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleJoinMeeting(selectedTicket.metting_link)}
                        style={{ textTransform: 'none', fontSize: '11px', borderRadius: '8px' }}
                      >
                        Join Meeting
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            </Box>
          )}
        </DialogContent>
        <DialogActions className="border-t border-gray-200 p-4">
          <Button 
            onClick={handleCloseViewDialog}
            variant="outlined"
            style={{ textTransform: 'none', fontSize: '13px', borderRadius: '8px' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Chat Dialog */}
      <Dialog 
        open={openChatDialog && !!selectedTicket} 
        onClose={handleCloseChatDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ 
          style: { 
            borderRadius: 16,
            height: '80vh',
            maxHeight: 800,
            display: 'flex',
            flexDirection: 'column'
          } 
        }}
      >
        <DialogTitle 
          style={{ 
            fontSize: '18px', 
            fontWeight: 'bold', 
            color: '#1f2937',
            borderBottom: '2px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
            backgroundColor: '#10B981',
            color: 'white'
          }}
        >
          <div className="flex items-center gap-2">
            <ChatIcon style={{ fontSize: '22px' }} />
            <div>
              <Typography variant="h6" style={{ fontSize: '16px', fontWeight: 'bold', color: 'white' }}>
                Chat - Ticket #{selectedTicket?.meeting_id}
              </Typography>
              <Typography variant="body2" style={{ fontSize: '11px', color: 'rgba(255,255,255,0.9)' }}>
                {selectedTicket?.topic}
              </Typography>
            </div>
          </div>
          <IconButton 
            size="small" 
            onClick={handleCloseChatDialog}
            style={{ color: 'white' }}
          >
            <Close style={{ fontSize: '20px' }} />
          </IconButton>
        </DialogTitle>
        <DialogContent 
          style={{ 
            padding: 16, 
            backgroundColor: '#f9fafb',
            flexGrow: 1,
            overflowY: 'auto'
          }}
        >
          {loadingChat ? (
            <Box className="flex justify-center items-center h-full">
              <CircularProgress size={40} />
            </Box>
          ) : chatError ? (
            <Alert severity="error" className="mb-4">
              <Typography variant="body2" style={{ fontSize: '13px' }}>
                {chatError}
              </Typography>
            </Alert>
          ) : chatMessages.length === 0 ? (
            <Box className="flex flex-col items-center justify-center h-full">
              <ChatIcon style={{ fontSize: '48px', color: '#d1d5db' }} />
              <Typography variant="body1" className="text-gray-500 mt-4" style={{ fontSize: '14px' }}>
                No messages yet. Start the conversation!
              </Typography>
            </Box>
          ) : (
            <div className="space-y-3">
              {chatMessages.map((message, index) => {
                const senderInfo = getMessageSenderInfo(message);
                const isFirstInGroup = index === 0 || 
                  getMessageSenderInfo(chatMessages[index - 1]).isSpoc !== senderInfo.isSpoc;

                return (
                  <div 
                    key={message.id || index}
                    className={`flex ${senderInfo.isSpoc ? 'justify-start' : 'justify-end'}`}
                  >
                    <div className={`flex gap-2 max-w-[75%] ${senderInfo.isSpoc ? 'flex-row' : 'flex-row-reverse'}`}>
                      {isFirstInGroup ? (
                        <Avatar 
                          className="w-8 h-8"
                          style={{ 
                            background: senderInfo.avatarBg,
                            fontSize: '12px',
                            fontWeight: 'bold',
                            flexShrink: 0
                          }}
                        >
                          {senderInfo.label.charAt(0)}
                        </Avatar>
                      ) : (
                        <div className="w-8 flex-shrink-0" />
                      )}
                      <div className="flex-1">
                        {isFirstInGroup && (
                          <div className={`flex items-center gap-2 mb-1 ${senderInfo.isSpoc ? 'justify-start' : 'justify-end'}`}>
                            <Chip 
                              label={senderInfo.label} 
                              size="small"
                              style={{ 
                                backgroundColor: senderInfo.bgColor,
                                color: senderInfo.color,
                                fontSize: '10px',
                                height: '20px',
                                fontWeight: 'bold'
                              }}
                            />
                          </div>
                        )}
                        <Card 
                          className={`p-3 shadow-md ${
                            senderInfo.isSpoc 
                              ? 'bg-white border-l-4' 
                              : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white'
                          }`}
                          style={{ 
                            borderRadius: 12,
                            borderLeftColor: senderInfo.isSpoc ? senderInfo.color : 'transparent'
                          }}
                        >
                          <div className="flex items-start justify-between">
                            <Typography 
                              variant="body2" 
                              style={{ 
                                fontSize: '13px',
                                color: senderInfo.isSpoc ? '#1f2937' : 'white',
                                wordWrap: 'break-word',
                                flex: 1
                              }}
                            >
                              {message.message}
                            </Typography>
                            {message.sending && (
                              <div className="ml-2 flex-shrink-0">
                                <CircularProgress 
                                  size={12} 
                                  style={{ 
                                    color: senderInfo.isSpoc ? '#9ca3af' : 'rgba(255,255,255,0.8)' 
                                  }} 
                                />
                              </div>
                            )}
                          </div>
                          <Typography 
                            variant="caption" 
                            style={{ 
                              fontSize: '10px',
                              color: senderInfo.isSpoc ? '#9ca3af' : 'rgba(255,255,255,0.8)',
                              marginTop: 8,
                              display: 'block'
                            }}
                          >
                            {formatChatTimestamp(message.created_at)}
                          </Typography>
                        </Card>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={chatMessagesEndRef} />
            </div>
          )}
        </DialogContent>
        <DialogActions 
          style={{ 
            flexShrink: 0,
            backgroundColor: 'white',
            padding: 16,
            borderTop: '2px solid #e5e7eb'
          }}
        >
          <Box className="flex flex-col gap-2 w-full">
            <Box className="flex gap-2">
              <TextField
                fullWidth
                multiline
                maxRows={3}
                variant="outlined"
                placeholder="Type your message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendChatMessage();
                  }
                }}
                disabled={sendingMessage || connectionStatus !== 'connected'}
                size="small"
                InputProps={{
                  style: { fontSize: '13px', borderRadius: '12px' }
                }}
              />
              <Button
                variant="contained"
                onClick={sendChatMessage}
                disabled={!newMessage.trim() || sendingMessage || connectionStatus !== 'connected'}
                style={{ 
                  borderRadius: '12px',
                  minWidth: 100,
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  textTransform: 'none',
                  fontSize: '13px',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 6px rgba(16, 185, 129, 0.3)'
                }}
              >
                {sendingMessage ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <>
                    <Send style={{ fontSize: '16px', marginRight: 4 }} />
                    Send
                  </>
                )}
              </Button>
            </Box>
            <Typography variant="caption" style={{ fontSize: '10px', color: '#6b7280' }}>
              Press Enter to send, Shift+Enter for new line
            </Typography>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Solve Dialog */}
      <Dialog 
        open={openSolveDialog} 
        onClose={handleCloseSolveDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ style: { borderRadius: 16 } }}
      >
        <DialogTitle 
          style={{ 
            fontSize: '18px', 
            fontWeight: 'bold', 
            color: '#1f2937',
            borderBottom: '2px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div className="flex items-center gap-2">
            <CheckCircle style={{ fontSize: '22px', color: '#10b981' }} />
            <Typography variant="h6" style={{ fontSize: '16px', fontWeight: 'bold' }}>
              Mark as Solved
            </Typography>
          </div>
          <IconButton size="small" onClick={handleCloseSolveDialog}>
            <Close style={{ fontSize: '20px' }} />
          </IconButton>
        </DialogTitle>
        <DialogContent className="pt-4">
          {solveSuccess ? (
            <Alert severity="success" className="mb-4">
              <Typography variant="body2" style={{ fontSize: '13px' }}>
                {solveSuccess}
              </Typography>
            </Alert>
          ) : null}
          {solveError && (
            <Alert severity="error" className="mb-4">
              <Typography variant="body2" style={{ fontSize: '13px' }}>
                {solveError}
              </Typography>
            </Alert>
          )}
          <Typography variant="body2" style={{ fontSize: '13px', color: '#6b7280', marginBottom: 16 }}>
            Please provide a closing remark explaining how this support ticket was resolved.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            placeholder="Enter closing remark (e.g., Issue resolved via phone call, problem was...)"
            value={closingRemark}
            onChange={(e) => setClosingRemark(e.target.value)}
            disabled={solvingLoading || solveSuccess}
            style={{ marginBottom: 16 }}
            InputProps={{
              style: { fontSize: '13px' }
            }}
          />
          {selectedTicket && (
            <Card style={{ padding: 12, borderRadius: 8, backgroundColor: '#f9fafb' }}>
              <Typography variant="caption" style={{ fontSize: '11px', color: '#6b7280', marginBottom: 8, display: 'block' }}>
                Ticket Details
              </Typography>
              <Typography variant="body2" style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: 4 }}>
                Ticket #{selectedTicket.meeting_id}
              </Typography>
              <Typography variant="body2" style={{ fontSize: '12px', marginBottom: 4 }}>
                {selectedTicket.topic}
              </Typography>
              <Typography variant="caption" style={{ fontSize: '10px', color: '#6b7280' }}>
                Student: {selectedTicket.student_name}
              </Typography>
            </Card>
          )}
        </DialogContent>
        <DialogActions className="border-t border-gray-200 p-4">
          <Button 
            onClick={handleCloseSolveDialog}
            variant="outlined"
            disabled={solvingLoading}
            style={{ textTransform: 'none', fontSize: '13px', borderRadius: '8px' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSolveMeeting}
            variant="contained"
            disabled={solvingLoading || solveSuccess || !closingRemark.trim()}
            style={{ 
              backgroundColor: solveSuccess ? '#10b981' : '#10b981',
              color: 'white',
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 'bold',
              borderRadius: '8px',
              padding: '8px 24px'
            }}
          >
            {solvingLoading ? 'Saving...' : solveSuccess ? 'Solved!' : 'Mark as Solved'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MyAssignedTickets;
